import type { PromotionType } from './promotions';

/**
 * Is this promotion internally consistent enough to put in front of a guest?
 *
 * Every field was validated in isolation and nothing checked them against each other, so
 * the editor happily saved a "Bundle Deal" containing no items and no price, a "Happy
 * Hour" with no discount at all, and a date range that ended before it began. Each one
 * renders as a card a guest taps and gets nothing from.
 *
 * These are contradictions, not opinions. Nothing here encodes a preference about how an
 * offer *should* be run — only that it cannot be the thing it claims to be.
 */

/** What is wrong, in words the owner can act on. */
export interface CoherenceProblem {
  field: string;
  message: string;
}

/**
 * Only the fields these rules actually read.
 *
 * Narrower than `Promotion` on purpose, so a create DTO, an update patch merged onto a row,
 * and a stored promotion all satisfy it without casting — they differ in the optionality of
 * fields nothing here looks at.
 */
export interface PromotionShape {
  promotion_type?: PromotionType;
  is_active?: boolean;
  discount_percentage?: number | null;
  discount_amount?: number | null;
  bundle_items?: unknown[] | null;
  bundle_price?: number | null;
  buffet_categories?: unknown[] | null;
  valid_from_date?: string | null;
  valid_to_date?: string | null;
}

const has = (n: number | null | undefined) => typeof n === 'number' && n > 0;

export function findCoherenceProblems(p: PromotionShape): CoherenceProblem[] {
  const problems: CoherenceProblem[] = [];
  const type = p.promotion_type;

  if (type === 'discount' || type === 'happy_hour') {
    const pct = has(p.discount_percentage);
    const amt = has(p.discount_amount);

    if (!pct && !amt) {
      problems.push({
        field: 'discount_percentage',
        message: 'Add a discount — a percentage or a fixed amount off.',
      });
    }
    if (pct && amt) {
      // Both set is ambiguous, and the guest app silently picks the percentage. The owner
      // who typed two numbers is not told which one guests will actually see.
      problems.push({
        field: 'discount_amount',
        message: 'Choose either a percentage or a fixed amount, not both.',
      });
    }
  }

  if (type === 'bundle') {
    if ((p.bundle_items ?? []).length === 0) {
      problems.push({ field: 'bundle_items', message: 'Add at least one item to the bundle.' });
    }
    if (!has(p.bundle_price)) {
      problems.push({ field: 'bundle_price', message: 'Set the price guests pay for the bundle.' });
    }
  }

  if (type === 'buffet' && (p.buffet_categories ?? []).length === 0) {
    problems.push({
      field: 'buffet_categories',
      message: 'Add at least one buffet category, such as Starters or Mains.',
    });
  }

  const from = p.valid_from_date?.slice(0, 10);
  const to = p.valid_to_date?.slice(0, 10);
  if (from && to && from > to) {
    problems.push({ field: 'valid_to_date', message: 'The end date is before the start date.' });
  }

  return problems;
}

/**
 * Does this change need to be coherent?
 *
 * **Switching something OFF is always allowed.** An owner who discovers a broken offer on
 * a guest's phone reaches for the toggle first, and a validation error at that moment
 * would trap them with the broken thing live — the exact opposite of what the check is
 * for. The same applies to editing something already inactive: it is a draft, and drafts
 * are allowed to be half-finished.
 */
export function mustBeCoherent(merged: PromotionShape): boolean {
  return merged.is_active !== false;
}
